import React, { FC, type RefObject, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import {
    setClearCountCheckedProducts,
    setCountCheckedProducts,
    setIsShowAddressRequiredPlank,
} from '~/src/redux/lead/lead.slice';
import { selectIsShowAddressRequiredPlank } from '~/src/redux/lead/lead.selectors';
import { SectionContainerLeadPage, TitleLeadPage } from '~/src/styles/shared-css/common-lead.styles';
import { ProductList } from '~/src/components/blocks/referral-products/products/components/product-list/product-list';
import {
    getProductCodesFromUrlParams,
    getSeparatedProducts,
} from '~/src/components/blocks/referral-products/products/products-block.utils';
import { onSendProductClickAnalytics } from './components/product-list/product-list.analytics';
import { COLORS } from '~/src/styles/tokens/colors';
import { ClientsFormEnum } from '~/src/models/enums/clients-form.enum';
import { MTS_INTERNET } from '~/src/models/constants/product-codes';
import {
    hideText,
    productBlockTitle,
    productsUrlQuery,
    rotateToRight,
    rotateToUp,
    showAnotherProductsText,
} from '~/src/components/blocks/referral-products/products/products-block.constants';

import { useClientRequestForm } from '~/src/context/client-request-form/use-client-request-form.hook';
import { SvgArrowDownBig } from '~/src/components/svg-components/arrow-down-big';
import type {
    DataProductsType,
    ProductsListByParityType,
} from '~/src/components/blocks/referral-products/products/products-blok.types';
import * as Styled from './products-block.styles';

interface AgentBlockProps {
    dataProducts: DataProductsType;
    productsListErrorRef: RefObject<HTMLDivElement>;
    needReset?: boolean;
    onResetComplete?: () => void;
    onCheckedProductsChange?: (codes: string[]) => void;
}

export const ProductsBlock: FC<AgentBlockProps> = ({
    dataProducts,
    productsListErrorRef,
    needReset,
    onResetComplete,
    onCheckedProductsChange,
}) => {
    const router = useRouter();
    const dispatch = useDispatch();
    const productsByUrlParamsHasBeenSet = useRef(false);
    const [dataProductsEntriesSeparated, setDataProductsEntriesSeparated] = useState<ProductsListByParityType>({
        odd: [],
        even: [],
    });
    const [currentTooltipId, setCurrentTooltipId] = useState(0);
    const [productsIsCollapsed, setProductsIsCollapsed] = useState(!!router.query[productsUrlQuery]);
    const [checkedProductCodes, setCheckedProductCodes] = useState<string[]>([]);

    const [addressRequiredData, setAddressRequiredData] = useState<null[]>([]);
    const isShowAddressRequiredPlank = useSelector(selectIsShowAddressRequiredPlank);
    const [isShow, setIsShow] = useState(false);
    const [isInitProductState, setIsInitProductState] = useState(true);
    const { state } = useClientRequestForm();
    const onCheckedProductsChangeRef = useRef(onCheckedProductsChange);
    onCheckedProductsChangeRef.current = onCheckedProductsChange;

    const mtsInternetProduct = Object.values(dataProducts)
        .flat(1)
        .find((product) => product?.code === MTS_INTERNET);

    useEffect(() => {
        if (needReset) {
            setCheckedProductCodes([]);
            setAddressRequiredData([]);
            setIsInitProductState(true);
            productsByUrlParamsHasBeenSet.current = false;
            onResetComplete?.();
        }
    }, [needReset]);

    useEffect(() => {
        onCheckedProductsChangeRef.current?.(checkedProductCodes);
    }, [checkedProductCodes]);

    useEffect(() => {
        const setInitialDataProductsEntriesSeparated = (productCodesFromUrlParams: string[]) => {
            const { data } = getSeparatedProducts({
                dataProducts,
                checkedProductCodes: productCodesFromUrlParams,
                productsIsCollapsed,
                isInit: true,
            });

            setDataProductsEntriesSeparated(data);
        };

        const productCodesFromUrlParams = getProductCodesFromUrlParams(router.query);
        let finalProductCodes: string[] = [];

        if (state.currentVariant === ClientsFormEnum.FORM_SELF_EMPLOYED) {
            finalProductCodes = productCodesFromUrlParams.includes(MTS_INTERNET) 
                ? productCodesFromUrlParams 
                : [...productCodesFromUrlParams, MTS_INTERNET];
        } else {
            finalProductCodes = productCodesFromUrlParams;
        }

        if (!productsByUrlParamsHasBeenSet.current && finalProductCodes.length) {
            setInitialDataProductsEntriesSeparated(finalProductCodes);
            productsByUrlParamsHasBeenSet.current = true;
        } else {
            if (finalProductCodes.length && !checkedProductCodes.length) {
                setInitialDataProductsEntriesSeparated(finalProductCodes);
                return;
            }

            if (productsIsCollapsed && !checkedProductCodes.length) {
                const dataProductsEntries = Object.entries(dataProducts);
                const groupWithProducts = dataProductsEntries.find((products) => {
                    return Array.isArray(products[1]) && products[1].length;
                });

                setDataProductsEntriesSeparated({ even: [], odd: [groupWithProducts || dataProductsEntries[0]] });
                return;
            }

            const { data } = getSeparatedProducts({
                dataProducts,
                checkedProductCodes,
                productsIsCollapsed,
            });

            setDataProductsEntriesSeparated(data);
        }

        if (!isShow) {
            setIsShow(true);
        }
    }, [dataProducts, productsIsCollapsed, state.currentVariant, productsByUrlParamsHasBeenSet.current]);

    useEffect(() => {
        if (state.currentVariant) {
            handleReset();
            if (state.currentVariant === ClientsFormEnum.FORM_SELF_EMPLOYED) {
                mtsInternetProduct &&
                    handleChangeCheckbox(
                        mtsInternetProduct.addressRequired,
                        true,
                        mtsInternetProduct?.name,
                        mtsInternetProduct?.code,
                    );
            }
        }
    }, [state.currentVariant]);

    useEffect(() => {
        checkedProductCodes.length && setIsInitProductState(false);
    }, [checkedProductCodes]);

    useEffect(() => {
        if (addressRequiredData.length && !isShowAddressRequiredPlank) {
            dispatch(setIsShowAddressRequiredPlank(true));
        } else if (isShowAddressRequiredPlank && !addressRequiredData.length) {
            dispatch(setIsShowAddressRequiredPlank(false));
        }
    }, [addressRequiredData]);

    const handleMouseOverTooltip = (id: number) => {
        setCurrentTooltipId(id);
    };

    const handleChangeCheckbox = (
        addressRequired: boolean,
        isChecked: boolean,
        productName: string,
        productCode: string,
    ) => {
        onSendProductClickAnalytics(isChecked, productName);
        addressRequired && isChecked && setAddressRequiredData((prev) => [...prev, null]);
        dispatch(setCountCheckedProducts(isChecked ? Number(isChecked) : -Number(!isChecked)));

        if (!isChecked && addressRequired) {
            setAddressRequiredData((prev) => {
                const newData = [...prev];
                newData.pop();
                return newData;
            });
        }

        if (isChecked) {
            setCheckedProductCodes((prev) => [...prev, productCode]);
        } else {
            setCheckedProductCodes((prev) => prev.filter((code) => code !== productCode));
        }
    };

    const handleReset = () => {
        setCheckedProductCodes([]);
        setAddressRequiredData([]);
        setIsInitProductState(true);
        productsByUrlParamsHasBeenSet.current = false;
        setProductsIsCollapsed(!!router.query[productsUrlQuery]);
        dispatch(setClearCountCheckedProducts());
    };

    if (!isShow) {
        return null;
    }

    return (
        <SectionContainerLeadPage>
            <TitleLeadPage>{productBlockTitle}</TitleLeadPage>
            <Styled.MainProductsContainer productsIsSelected={!!checkedProductCodes.length || isInitProductState}>
                <Styled.ProductsContainer>
                    <ProductList
                        dataProducts={dataProductsEntriesSeparated?.odd}
                        checkedProductCodes={checkedProductCodes}
                        onChangeCheckbox={handleChangeCheckbox}
                        currentId={currentTooltipId}
                        onMouseOverTooltip={handleMouseOverTooltip}
                        changeCheckedProductCodes={setCheckedProductCodes}
                        currentVariant={state.currentVariant}
                    />
                    {!!dataProductsEntriesSeparated?.even.length && (
                        <ProductList
                            dataProducts={dataProductsEntriesSeparated?.even}
                            checkedProductCodes={checkedProductCodes}
                            onChangeCheckbox={handleChangeCheckbox}
                            currentId={currentTooltipId}
                            onMouseOverTooltip={handleMouseOverTooltip}
                            changeCheckedProductCodes={setCheckedProductCodes}
                            currentVariant={state.currentVariant}
                        />
                    )}
                </Styled.ProductsContainer>
                <Styled.CollapsedButtonContainer productsIsCollapsed={productsIsCollapsed}>
                    <Styled.CollapseButton
                        type="button"
                        onClick={() => {
                            setProductsIsCollapsed((prev) => !prev);
                        }}
                    >
                        <Styled.CollapseButtonText>
                            {productsIsCollapsed ? showAnotherProductsText : hideText}
                        </Styled.CollapseButtonText>
                        <Styled.CollapseBtnArrowContainer rotate={productsIsCollapsed ? rotateToRight : rotateToUp}>
                            <SvgArrowDownBig color={COLORS.textPrimaryLink} />
                        </Styled.CollapseBtnArrowContainer>
                    </Styled.CollapseButton>
                </Styled.CollapsedButtonContainer>
            </Styled.MainProductsContainer>
            {!checkedProductCodes.length && !isInitProductState && (
                <Styled.NotSelectedProductsError ref={productsListErrorRef}>
                    Выберите один или несколько продуктов
                </Styled.NotSelectedProductsError>
            )}
        </SectionContainerLeadPage>
    );
};
